// Control characters other than tab / LF / CR never appear in real question
// content, but show up when plain text is wrongly decoded as base64.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/;

/**
 * Best-effort check for whether `str` is base64-encoded UTF-8 text.
 *
 * NOTE: this is a heuristic. Short plain words can be valid base64 too
 * ("True", "TRUE", "None"). The transport needs an unambiguous rule (backend
 * always encodes, or sends an encoding flag) so this guess can be removed.
 * Until then we require all of:
 *   1. the base64 alphabet/padding,
 *   2. decoded bytes that are valid UTF-8 ("True" -> "N\xBB\x9E" fails),
 *   3. no control characters in the decoded text ("TRUE" -> "M\x15\x04" fails).
 */
export const isBase64 = (str: string) => {
  if (!str) {
    return false;
  }
  const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
  if (!base64regex.test(str)) {
    return false;
  }
  try {
    const bytes = Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return !CONTROL_CHARS.test(decoded);
  } catch {
    return false;
  }
};

//export const base64Decode = (str: string): string =>
//str ? Buffer.from(str, 'base64').toString('binary') : str;
//export const base64Encode = (str: string): string =>
//str ? Buffer.from(str, 'binary').toString('base64') : str;

export const base64Decode = (str: string): string => {
  const decodedBytes = Uint8Array.from(atob(str), (c) => c.charCodeAt(0)); // Decode Base64 to bytes
  const decoder = new TextDecoder();
  const decodedString = decoder.decode(decodedBytes); // Convert bytes back to UTF-8 string
  return decodedString;
};

export const base64Encode = (str: string): string => {
  if (str === undefined || str === null) {
    return str;
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(str); // Convert to UTF-8 bytes
  // Build the binary string in chunks. Spreading the whole array into
  // String.fromCharCode throws "Maximum call stack size exceeded" for large
  // inputs (e.g. questions with embedded images), which silently broke saving.
  const CHUNK_SIZE = 0x8000;
  let binary = '';
  for (let i = 0; i < data.length; i += CHUNK_SIZE) {
    binary += String.fromCharCode(...data.subarray(i, i + CHUNK_SIZE));
  }
  return btoa(binary); // Base64 encode the bytes
};
